import type {PayloadAction} from '@reduxjs/toolkit'
import {createSlice} from '@reduxjs/toolkit'

/**
 * Persisted column settings for a single table view.
 * Each entry is keyed by `${tabId}__${sectionId}` in the store.
 */
export interface TableColumnSettings {
  /** Map of columnKey → visible (true/false). Only configurable columns are stored. */
  columnVisibility: Record<string, boolean>
  /** Ordered list of visible configurable column keys in display order. */
  columnOrder: string[]
  /** ISO timestamp of the last update (for conflict resolution with backend). */
  updatedAt?: string
}

export interface TableSettingsState {
  /**
   * Key format: `${tabId}__${sectionId}`
   * e.g. "engineering-performance__commercial-and-consumer"
   */
  settings: Record<string, TableColumnSettings>
}

const initialState: TableSettingsState = {
  settings: {},
}

const tableSettingsSlice = createSlice({
  name: 'tableSettings',
  initialState,
  reducers: {
    /**
     * Save or update column settings for a specific table view.
     */
    setTableSettings: (
      state,
      action: PayloadAction<{
        preferenceKey: string
        columnVisibility: Record<string, boolean>
        columnOrder: string[]
      }>,
    ) => {
      const {preferenceKey, columnVisibility, columnOrder} = action.payload
      state.settings[preferenceKey] = {
        columnVisibility,
        columnOrder,
        updatedAt: new Date().toISOString(),
      }
    },

    /**
     * Bulk-load settings (e.g. from backend API response or localStorage hydration).
     */
    loadAllTableSettings: (state, action: PayloadAction<Record<string, TableColumnSettings>>) => {
      // Merge instead of replace so locally-persisted settings survive if
      // the API response is incomplete (e.g. missing some department/tab combos).
      state.settings = {...state.settings, ...action.payload}
    },

    /**
     * Reset a specific table's settings back to defaults (removes the stored entry).
     */
    resetTableSettings: (state, action: PayloadAction<string>) => {
      delete state.settings[action.payload]
    },

    /**
     * Reset all table settings back to defaults.
     */
    resetAllTableSettings: state => {
      state.settings = {}
    },
  },
})

export const {setTableSettings, loadAllTableSettings, resetTableSettings, resetAllTableSettings} =
  tableSettingsSlice.actions

export default tableSettingsSlice.reducer
